/**
 * PassageInkLayer — draws annotation marks ABOVE the passage, never on it.
 *
 * THE INVARIANT THIS EXISTS TO PROTECT: PSLE passages number fixed physical lines and students
 * answer "line 7". HighlightablePassageText documents the rule — spans and marks carry no
 * padding, margin, or border, so under `whitespace-pre` the numbered lines never shift. A box
 * or circle drawn as a CSS border on a word would violate exactly that: it would widen the word
 * by a few pixels, nudge the rest of the line, and silently break every line reference in the
 * passage.
 *
 * So marks are geometry, not decoration. Each one is measured from the word spans it covers and
 * painted into an SVG layer sitting over the text. The passage's own layout is untouched, which
 * is also why the layer is `pointer-events: none` — the gesture below it must still receive
 * every pointer event.
 *
 * WHY GEOMETRY IS WRITTEN IMPERATIVELY. React renders one element per mark — it knows the shape
 * and the id from `marks` alone — but the coordinates come from measuring the DOM, and feeding a
 * measurement back through state would re-render the whole layer on every resize. The effects
 * below therefore set attributes on the already-rendered nodes, which is what an effect is for:
 * pushing current values into an external system. Nothing here calls setState.
 *
 * MEASUREMENT TIMING. Rects are only meaningful once the reading face has loaded: the passage
 * requests Lora with `display=swap`, so the first paint uses a fallback typeface whose metrics
 * differ, and a mark measured then would sit a few pixels off once Lora arrives. The layer
 * therefore re-measures on `document.fonts.ready`, on container resize, and on window resize.
 *
 * jsdom returns zeroed rects for everything, so under test the shapes render with degenerate
 * geometry rather than crashing. Each one carries `data-mark-id`, which is how tests assert what
 * was drawn without needing real layout.
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import {
  isTwoEndedShape,
  type AnnotationMark,
  type AnnotationShape,
} from '@/lib/passageAnnotations';

export interface PassageInkLayerProps {
  marks: AnnotationMark[];
  /** The element containing the `data-offset` word spans — normally the passage <p>. */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Passage text. Only a re-measure trigger: new text means the word spans were rebuilt. */
  passage: string;
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * A small deterministic wobble per mark, so a circle reads as something a child drew rather than
 * as a generated ellipse. Same mark, same wobble, every re-measure — a random offset would make
 * marks jitter on every resize.
 */
function wobble(seed: number, spread: number): number {
  const n = Math.sin(seed * 12.9898) * 43758.5453;
  return (n - Math.floor(n) - 0.5) * 2 * spread;
}

/**
 * The union of the word spans a mark covers, in the container's own content coordinates.
 *
 * Uses offsetLeft/offsetTop rather than getBoundingClientRect deliberately. The SVG is sized
 * from the container's scroll dimensions, which are LAYOUT pixels; bounding rects are VISUAL
 * pixels, and the two diverge inside any scaling context (an ancestor using the CSS `zoom`
 * property, or a scale transform). Mixing them would draw every mark in the wrong place there.
 * Offsets are layout pixels too, so both sides of the calculation agree whatever the page does.
 *
 * Offsets are already relative to the nearest positioned ancestor — the passage <p> — and are
 * not reduced by scrolling, so they need no rect subtraction and no scroll correction.
 */
function measureMark(container: HTMLElement, mark: AnnotationMark): Box | null {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;

  const spans = container.querySelectorAll<HTMLElement>('[data-offset]');
  spans.forEach((span) => {
    const offset = Number(span.getAttribute('data-offset'));
    if (!Number.isInteger(offset) || offset < mark.start || offset >= mark.end) return;
    left = Math.min(left, span.offsetLeft);
    top = Math.min(top, span.offsetTop);
    right = Math.max(right, span.offsetLeft + span.offsetWidth);
    bottom = Math.max(bottom, span.offsetTop + span.offsetHeight);
  });

  if (left === Infinity) return null; // Mark covers no rendered word.

  return { x: left, y: top, w: right - left, h: bottom - top };
}

/**
 * The attributes that place one shape. Returns one entry per stroke — a bracket is two, every
 * other shape is one.
 */
function geometryFor(shape: AnnotationShape, id: number, box: Box): Record<string, string>[] {
  const x = box.x + wobble(id, 1.1);
  const y = box.y + wobble(id + 7, 1.1);
  const { w, h } = box;

  switch (shape) {
    case 'circle': {
      const cx = x + w / 2;
      const cy = y + h / 2;
      return [
        {
          cx: String(cx),
          cy: String(cy),
          rx: String(w / 2 + 5),
          ry: String(h / 2 + 1.5),
          transform: `rotate(${wobble(id + 3, 1.2).toFixed(2)} ${cx} ${cy})`,
        },
      ];
    }
    case 'box':
      return [
        {
          x: String(x - 3),
          y: String(y + 1),
          width: String(Math.max(w + 6, 1)),
          height: String(Math.max(h - 2, 1)),
          rx: '3',
        },
      ];
    case 'underline':
      return [{ d: underlinePath(x, y, w, h) }];
    case 'squiggly': {
      // The "I don't understand this word" mark. Deliberately a visibly different stroke from a
      // plain underline, since the two mean opposite things to a student.
      const baseY = y + h - 2.5;
      const period = 6;
      const amplitude = 2.2;
      let d = `M ${x - 1} ${baseY}`;
      for (let cursor = 0; cursor < w + 2; cursor += period) {
        const half = Math.min(period / 2, w + 2 - cursor);
        d += ` q ${half / 2} ${-amplitude} ${half} 0 q ${half / 2} ${amplitude} ${half} 0`;
      }
      return [{ d }];
    }
    case 'squareBracket':
    case 'roundBracket': {
      // The guide draws both bracket kinds WITH the phrase underlined inside them, so the
      // underline is part of the mark rather than a separate one the student has to add.
      const top = y + 1.5;
      const bottom = y + h - 1.5;
      const left = x - 3;
      const right = x + w + 3;
      const arm = 4.5;

      const strokes =
        shape === 'squareBracket'
          ? [
              { d: `M ${left + arm} ${top} H ${left} V ${bottom} H ${left + arm}` },
              { d: `M ${right - arm} ${top} H ${right} V ${bottom} H ${right - arm}` },
            ]
          : [
              // Round brackets: a single curve each side rather than three segments.
              {
                d: `M ${left + arm} ${top} Q ${left - 1} ${(top + bottom) / 2} ${left + arm} ${bottom}`,
              },
              {
                d: `M ${right - arm} ${top} Q ${right + 1} ${(top + bottom) / 2} ${right - arm} ${bottom}`,
              },
            ];

      return [...strokes, { d: underlinePath(x, y, w, h) }];
    }
    case 'question':
    case 'remind':
    case 'surprise': {
      // A circled glyph. On paper these live in the margin; the passage sheet has none, so the
      // marker goes just past the words it is about and RAISED into the leading, which is the
      // only space on a line-numbered passage that carries no text. Sitting it beside the words
      // at their own height would cover whatever follows them.
      const r = 6.5;
      const cx = box.x + box.w + r - 1;
      // Raised into the leading above the words. On the first line there is none — and the
      // passage clips anything above it, since overflow-x:auto makes the vertical axis clip
      // too — so it is clamped down to the top edge rather than dropped below the line.
      // Dropping below would put it in the same gap the NEXT line's markers rise into, and two
      // markers would collide there.
      const cy = Math.max(box.y - 4, r + 0.5);
      return [
        { cx: String(cx), cy: String(cy), r: String(r) },
        // dominant-baseline centres the letter on cy across engines.
        {
          x: String(cx),
          y: String(cy),
          'font-size': '10',
          'text-anchor': 'middle',
          'dominant-baseline': 'central',
        },
      ];
    }
    default:
      return [];
  }
}

/**
 * The double-headed arrow joining a pronoun to what it refers to.
 *
 * Drawn as one curve with an arrowhead at each end. It bows AWAY from the text — upward when
 * both ends sit on the same line, so it arcs through the leading rather than across the words;
 * sideways when they are lines apart, so it does not scribble over everything between.
 */
function linkGeometry(from: Box, to: Box): Record<string, string>[] {
  const sameLine = Math.abs(from.y - to.y) < from.h / 2;

  const ax = from.x + from.w / 2;
  const bx = to.x + to.w / 2;
  const ay = sameLine ? from.y : from.y + from.h / 2;
  const by = sameLine ? to.y : to.y + to.h / 2;

  const midX = (ax + bx) / 2;
  const midY = sameLine ? Math.min(ay, by) - Math.max(14, Math.abs(bx - ax) * 0.12) : (ay + by) / 2;
  // Across lines, bow out to the left of both ends so the curve clears the text between them.
  const control = sameLine
    ? { x: midX, y: midY }
    : { x: Math.min(ax, bx) - Math.max(18, Math.abs(by - ay) * 0.35), y: midY };

  const head = (tipX: number, tipY: number, fromX: number, fromY: number): string => {
    const angle = Math.atan2(tipY - fromY, tipX - fromX);
    const size = 5;
    const spread = 0.42;
    const p1x = tipX - size * Math.cos(angle - spread);
    const p1y = tipY - size * Math.sin(angle - spread);
    const p2x = tipX - size * Math.cos(angle + spread);
    const p2y = tipY - size * Math.sin(angle + spread);
    return `M ${p1x} ${p1y} L ${tipX} ${tipY} L ${p2x} ${p2y}`;
  };

  return [
    { d: `M ${ax} ${ay} Q ${control.x} ${control.y} ${bx} ${by}` },
    { d: head(ax, ay, control.x, control.y) },
    { d: head(bx, by, control.x, control.y) },
  ];
}

/** A slight double curve, so it reads as a pen stroke rather than a CSS border. */
function underlinePath(x: number, y: number, w: number, h: number): string {
  const baseY = y + h - 2.5;
  return `M ${x - 1} ${baseY} Q ${x + w * 0.35} ${baseY + 1.6} ${x + w * 0.62} ${baseY - 0.4} T ${x + w + 1} ${baseY}`;
}

export function PassageInkLayer({ marks, containerRef, passage }: PassageInkLayerProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const frameRef = useRef<number | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const svg = svgRef.current;
    if (!container || !svg) return;

    const width = Math.max(container.scrollWidth, container.clientWidth);
    const height = Math.max(container.scrollHeight, container.clientHeight);
    svg.setAttribute('width', String(width));
    svg.setAttribute('height', String(height));
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

    marks.forEach((mark) => {
      const host = svg.querySelector<SVGElement>(`[data-mark-id="${mark.id}"]`);
      if (!host) return;

      const box = measureMark(container, mark);
      const targetBox =
        mark.targetStart !== undefined && mark.targetEnd !== undefined
          ? measureMark(container, {
              ...mark,
              start: mark.targetStart,
              end: mark.targetEnd,
            })
          : null;

      // Covers no rendered word — a stale mark against a changed passage. A link needs both.
      if (!box || (isTwoEndedShape(mark.shape) && !targetBox)) {
        host.style.display = 'none';
        return;
      }
      host.style.display = '';

      // Multi-part shapes are a <g>; the rest are one element. A marker's <g> holds a circle
      // and a letter rather than paths, so the children are taken in document order.
      const targets = host.tagName === 'g' ? (Array.from(host.children) as SVGElement[]) : [host];

      const geometry =
        isTwoEndedShape(mark.shape) && targetBox
          ? linkGeometry(box, targetBox)
          : geometryFor(mark.shape, mark.id, box);

      geometry.forEach((attrs, index) => {
        const target = targets[index];
        if (!target) return;
        Object.entries(attrs).forEach(([name, value]) => target.setAttribute(name, value));
      });

      // Shapes render hidden, so one restored from storage never paints at 0,0 for a frame
      // before its first measure lands.
      host.style.visibility = 'visible';
    });
  }, [marks, containerRef]);

  /** Coalesce bursts of resize callbacks into one measure per frame. */
  const scheduleMeasure = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      measure();
    });
  }, [measure]);

  // Layout effect so a NEW mark is placed before the browser paints it.
  //
  // It cannot be the only measure: this component is a child of the paragraph it measures, and
  // React attaches a host element's ref after its children's layout effects have run. On the
  // very first mount `containerRef.current` is still null here and this call does nothing — the
  // passive effect below is what catches that pass.
  useLayoutEffect(() => {
    measure();
  }, [measure, passage]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Refs are attached by the time passive effects run, so this is the measure that lands on
    // mount. It writes attributes only, so it cannot cascade a render.
    measure();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scheduleMeasure);
      observer.observe(container);
    }
    window.addEventListener('resize', scheduleMeasure);

    // The fallback typeface has different metrics from Lora; re-measure once Lora lands.
    let cancelled = false;
    document.fonts?.ready
      .then(() => {
        if (!cancelled) scheduleMeasure();
      })
      .catch(() => {
        // No font loading API, or the face failed to load. Existing geometry stays; marks sit a
        // couple of percent off at worst, which is still visibly around the right words.
      });

    return () => {
      cancelled = true;
      observer?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [containerRef, measure, scheduleMeasure]);

  if (marks.length === 0) return null;

  return (
    <svg
      ref={svgRef}
      aria-hidden="true"
      className="pointer-events-none absolute left-0 top-0 overflow-visible"
    >
      {marks.map((mark) => (
        <InkShape key={mark.id} mark={mark} />
      ))}
    </svg>
  );
}

/**
 * One mark, rendered without geometry — the effects above place it.
 *
 * Stroke colour is the student's pen: deliberately cool against the warm otter-earth passage
 * text, so a mark can never be misread as part of the printed passage.
 */
function InkShape({ mark }: { mark: AnnotationMark }) {
  const stroke = {
    fill: 'none',
    stroke: '#1f5aa8',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  const identity = {
    'data-mark-id': mark.id,
    'data-shape': mark.shape,
    // Hidden until the first measure places it.
    style: { visibility: 'hidden' as const },
  };

  switch (mark.shape) {
    case 'circle':
      return <ellipse {...stroke} {...identity} />;
    case 'box':
      return <rect {...stroke} {...identity} />;
    case 'underline':
    case 'squiggly':
      return <path {...stroke} {...identity} />;
    case 'roundBracket':
    case 'squareBracket':
      // Two bracket strokes plus the underline the guide draws between them.
      return (
        <g {...identity}>
          <path {...stroke} />
          <path {...stroke} />
          <path {...stroke} />
        </g>
      );
    case 'link':
      // The curve, then an arrowhead at each end.
      return (
        <g {...identity}>
          <path {...stroke} />
          <path {...stroke} />
          <path {...stroke} />
        </g>
      );
    case 'question':
    case 'remind':
    case 'surprise':
      return (
        <g {...identity}>
          <circle {...stroke} />
          <text fill="#1f5aa8" stroke="none" className="font-body font-bold">
            {MARKER_GLYPHS[mark.shape]}
          </text>
        </g>
      );
    default:
      return null;
  }
}

/** The letter inside each circled marker, straight from the school symbol sheet. */
const MARKER_GLYPHS: Record<'question' | 'remind' | 'surprise', string> = {
  question: '?',
  remind: 'R',
  surprise: '!',
};
